import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Flex, Form, Grid, Input, message, Row, Space, Spin, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { getRequestWithParams } from "../../../api/httpRequest";
import { useLocation } from "react-router";
import { Document, HeadingLevel, Packer, Paragraph, TextRun } from 'docx';
import { getBaseUrl } from '../../../api/axiosInstance';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface Task {
    id: string;
    question: string;
    answer: boolean;
    imageId?: string;
}

interface AnswerStatus {
    [key: string]: {
        answer: string;
        saved: boolean;
    };
}

interface FirstTourTabProps {
    firstTourPass?: boolean | null;
}

const FirstTourTab: React.FC<FirstTourTabProps> = ({ firstTourPass }) => {
    const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
    const [answers, setAnswers] = useState<AnswerStatus>({});
    const [isSaving, setIsSaving] = useState(false);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [allAnswersSubmitted, setAllAnswersSubmitted] = useState(false);
    const location = useLocation();
    const screens = useBreakpoint();

    useEffect(() => {
        const allAnswered = tasks.length > 0 && tasks.every(task => task.answer);
        setAllAnswersSubmitted(allAnswered);
    }, [tasks]);

    useEffect(() => {
        const fetchTasksAndAnswers = async () => {
            try {
                setLoading(true);
                const response = await getRequestWithParams('/server/olympiad/tasks', {
                    olympiadId: location.state.olympiad.id
                });

                if (response.data && Array.isArray(response.data)) {
                    const tasksData = response.data;

                    const initialAnswers: AnswerStatus = {};
                    tasksData.forEach(task => {
                        initialAnswers[task.id] = {
                            answer: task.answer || '',
                            saved: task.answer
                        };
                    });

                    setAnswers(initialAnswers);
                    setTasks(tasksData);

                    const firstUnansweredIndex = tasksData.findIndex(task => !task.answer);
                    setCurrentTaskIndex(firstUnansweredIndex !== -1 ? firstUnansweredIndex : 0);
                } else {
                    throw new Error('Неверный формат данных');
                }
            } catch (err) {
                console.error('Ошибка при загрузке задач:', err);
                setError('Не удалось загрузить задания. Пожалуйста, попробуйте позже.');
                message.error('Не удалось загрузить задания');
            } finally {
                setLoading(false);
            }
        };

        fetchTasksAndAnswers();
    }, [location.state.olympiad.id]);

    const currentTask = tasks[currentTaskIndex];

    const handleDownloadTasks = async () => {
        try {
            const doc = new Document({
                sections: [{
                    properties: {},
                    children: [
                        new Paragraph({
                            text: "Олимпиада по математике 2025 год.",
                            heading: HeadingLevel.HEADING_1,
                        }),
                        new Paragraph({
                            text: "Заочный тур",
                            heading: HeadingLevel.HEADING_2,
                        }),
                        ...tasks.flatMap((task, index) => [
                            new Paragraph({
                                text: `Задание ${index + 1}`,
                                heading: HeadingLevel.HEADING_3,
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: task.question,
                                        break: 1,
                                    }),
                                ],
                            }),
                            new Paragraph({ text: "" }),
                        ]),
                    ],
                }],
            });

            const blob = await Packer.toBlob(doc);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'Мат. Заочный тур.docx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            message.success('Файл с заданиями скачивается');
        } catch (error) {
            console.error('Ошибка при генерации файла:', error);
            message.error('Не удалось сгенерировать файл');
        }
    };

    const handleSaveAnswer = async () => {
        if (!currentTask) return;

        const answer = answers[currentTask.id]?.answer?.trim();
        if (!answer) {
            message.warning('Введите ответ перед сохранением');
            return;
        }

        setIsSaving(true);
        try {
            await getRequestWithParams('/server/olympiad/tasks/check', {
                taskId: currentTask.id,
                answer: answer
            });

            setAnswers(prev => ({
                ...prev,
                [currentTask.id]: {
                    answer: answer,
                    saved: true
                }
            }));

            setTasks(prevTasks => prevTasks.map(task =>
                task.id === currentTask.id ? { ...task, isAnswered: true, userAnswer: answer } : task
            ));

            message.success('Ответ сохранен!');

            const nextUnansweredIndex = tasks.findIndex((task, index) =>
                index > currentTaskIndex && !task.answer
            );

            if (nextUnansweredIndex !== -1) {
                setCurrentTaskIndex(nextUnansweredIndex);
            } else if (currentTaskIndex < tasks.length - 1) {
                setCurrentTaskIndex(currentTaskIndex + 1);
            }
        } catch (err) {
            console.error('Ошибка при сохранении ответа:', err);
            message.error('Произошла ошибка при сохранении ответа. Попробуйте позже.');
        } finally {
            setIsSaving(false);
        }
    };

    const getTaskStatuses = () => {
        return tasks.map(task => ({
            id: task.id,
            status: task.answer ? 'answered' : 'not_answered'
        }));
    };

    const renderProgressWithSegments = () => {
        const taskStatuses = getTaskStatuses();
        const segmentWidth = 100 / tasks.length;

        const answeredCount = taskStatuses.filter(ts => ts.status === 'answered').length;
        const notAnsweredCount = taskStatuses.filter(ts => ts.status === 'not_answered').length;

        return (
            <div style={{ marginTop: 16 }}>
                <div style={{
                    width: '100%',
                    height: 16,
                    backgroundColor: '#f0f0f0',
                    borderRadius: 6,
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    {taskStatuses.map((taskStatus, index) => {
                        const left = index * segmentWidth;
                        const backgroundColor = taskStatus.status === 'answered' ? '#1890ff' : '#f0f0f0';

                        return (
                            <div
                                key={taskStatus.id}
                                style={{
                                    position: 'absolute',
                                    left: `${left}%`,
                                    width: `${segmentWidth}%`,
                                    height: '100%',
                                    backgroundColor: backgroundColor,
                                    borderRight: '1px solid #fff'
                                }}
                                title={`Задача ${taskStatus.id}: ${taskStatus.status === 'answered' ? 'Ответ дан' : 'Ответ не дан'}`}
                            />
                        );
                    })}
                </div>

                <Flex gap="small" justify="center" style={{ marginTop: 12 }} wrap="wrap">
                    <Flex align="center" gap="small">
                        <div style={{ width: 10, height: 10, backgroundColor: '#1890ff', borderRadius: 2 }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>Ответ дан ({answeredCount})</Text>
                    </Flex>
                    <Flex align="center" gap="small">
                        <div style={{ width: 10, height: 10, backgroundColor: '#f0f0f0', borderRadius: 2 }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>Ответ не дан ({notAnsweredCount})</Text>
                    </Flex>
                </Flex>

                <Flex justify="center" style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Всего задач: {tasks.length} | Ответов дано: {answeredCount}
                    </Text>
                </Flex>
            </div>
        );
    };

    const handleAnswerChange = (value: string) => {
        if (!currentTask) return;

        setAnswers(prev => ({
            ...prev,
            [currentTask.id]: {
                answer: value,
                saved: prev[currentTask.id]?.saved || false
            }
        }));
    };

    const handleNextTask = () => {
        const nextUnansweredIndex = tasks.findIndex((task, index) =>
            index > currentTaskIndex && !task.answer
        );

        if (nextUnansweredIndex !== -1) {
            setCurrentTaskIndex(nextUnansweredIndex);
        } else if (currentTaskIndex < tasks.length - 1) {
            setCurrentTaskIndex(currentTaskIndex + 1);
        }
    };

    const handlePrevTask = () => {
        let prevUnansweredIndex = -1;
        for (let i = currentTaskIndex - 1; i >= 0; i--) {
            if (!tasks[i].answer) {
                prevUnansweredIndex = i;
                break;
            }
        }

        if (prevUnansweredIndex !== -1) {
            setCurrentTaskIndex(prevUnansweredIndex);
        } else if (currentTaskIndex > 0) {
            setCurrentTaskIndex(currentTaskIndex - 1);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Spin size="large" tip="Загрузка заданий..." />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 24 }}>
                <Alert
                    message="Ошибка загрузки"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={() => window.location.reload()}>
                            Попробовать снова
                        </Button>
                    }
                />
            </div>
        );
    }

    if (tasks.length === 0) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Text>Задания не найдены</Text>
            </div>
        );
    }

    const getImageUrl = (imageId: string) => {
        return `${getBaseUrl()}/server/olympiad/files/img?id=${imageId}`;
    };

    const getSecondTourDate = () => {
        if (location.state.olympiad.id === '1' || location.state.olympiad.id === '3') {
            return "14.02.2026"
        } else {
            return "15.02.2026"
        }
    }

    return (
        <div>
            <Title level={4} style={{ fontSize: screens.xs ? '16px' : '18px' }}>Информация о первом туре</Title>

            {firstTourPass !== null && (
                <Alert
                    message={firstTourPass ? "Первый тур пройден" : "Первый тур не пройден"}
                    description={firstTourPass 
                        ? "Поздравляем! Вы успешно прошли во второй тур олимпиады." 
                        : "К сожалению, вы не прошли во второй тур олимпиады."}
                    type={firstTourPass ? "success" : "error"}
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {allAnswersSubmitted && firstTourPass === null && (
                <Alert
                    message="Все ответы сохранены"
                    description="Вы ответили на все вопросы первого тура. Ожидайте результатов."
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card
                        title="Описание"
                        size="small"
                        extra={
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadTasks}
                                type="primary"
                                size={screens.xs ? "small" : "middle"}
                            >
                                {screens.xs ? "Скачать" : "Скачать задания"}
                            </Button>
                        }
                    >
                        <div style={{ whiteSpace: 'pre-wrap', fontSize: screens.xs ? '14px' : 'inherit' }}>
                            {"Вам представлено задания, и на их решение у вас будет всего одна попытка.\n" +
                                "Временных ограничений нет, поэтому вы можете распечатать задание для удобства.\n" +
                                `Срок окончания первого тура 06.02.2026\n` +
                                "Информацию о прохождении во второй (заключительный, очный) тур Вы сможете увидеть на данной странице.\n" +
                                "До подведения итогов Вы можете отредактировать свои данные, а также поменять площадку, на которой Вы хотите участвовать во втором (заключительном, очном) туре.\n" +
                                `Второй тур олимпиады состоится ${getSecondTourDate()}. Дополнительные сведения об участии во втором туре смотрите на вкладке второго тура.`}
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col span={24}>
                    <Card
                        title={`Задание ${currentTaskIndex + 1} из ${tasks.length}`}
                        size="default"
                        extra={
                            <Flex gap="small">
                                <Button
                                    size="small"
                                    onClick={handlePrevTask}
                                    disabled={currentTaskIndex === 0}
                                >
                                    Назад
                                </Button>
                                <Button
                                    size="small"
                                    onClick={handleNextTask}
                                    disabled={currentTaskIndex === tasks.length - 1}
                                >
                                    Вперед
                                </Button>
                            </Flex>
                        }
                    >
                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Вопрос:</Text>
                            <Flex 
                                gap="middle" 
                                align="flex-start" 
                                style={{ marginTop: 8 }}
                                vertical={!screens.xl}
                            >
                                {currentTask.imageId && (
                                    <div style={{
                                        flex: '0 0 auto',
                                        maxWidth: '100%',
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}>
                                        <img 
                                            src={getImageUrl(currentTask.imageId)}
                                            alt="Иллюстрация к задаче"
                                            style={{ 
                                                maxWidth: '100%', 
                                                maxHeight: 400, 
                                                borderRadius: 6,
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }} 
                                        />
                                    </div>
                                )}
                                <div style={{
                                    padding: 12,
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 6,
                                    whiteSpace: 'pre-wrap',
                                    fontSize: screens.xs ? '14px' : 'inherit',
                                    flex: 1
                                }}>
                                    {currentTask.question}
                                </div>
                            </Flex>
                        </div>

                        <Form layout="vertical">
                            <Form.Item label="Ваш ответ:">
                                <Flex
                                    gap="middle"
                                    align={screens.xs ? "start" : "flex-end"}
                                    vertical={screens.xs}
                                >
                                    <Input
                                        value={answers[currentTask.id]?.answer || ''}
                                        onChange={(e) => handleAnswerChange(e.target.value)}
                                        placeholder="Введите ваш ответ здесь..."
                                        style={{ width: screens.xs ? '100%' : 200 }}
                                        disabled={currentTask.answer}
                                    />
                                    <Button
                                        type="primary"
                                        onClick={handleSaveAnswer}
                                        loading={isSaving}
                                        disabled={currentTask.answer || answers[currentTask.id]?.saved}
                                        style={{ minWidth: screens.xs ? '100%' : 140 }}
                                    >
                                        {currentTask.answer || answers[currentTask.id]?.saved ? 'Ответ сохранен' : 'Сохранить ответ'}
                                    </Button>

                                    {(currentTask.answer || answers[currentTask.id]?.saved) && (
                                        <Space>
                                            <Text type="success" strong>
                                                Ответ сохранен
                                            </Text>
                                        </Space>
                                    )}
                                </Flex>
                            </Form.Item>
                        </Form>

                        {renderProgressWithSegments()}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default FirstTourTab;